import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, InputNumber, Select, message, Space, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;

interface RedemptionCode {
  id: number;
  code: string;
  amount: number;
  type: string;
  used_by_name: string;
  used_at: string;
  expires_at: string;
  created_at: string;
  created_by_name: string;
}

const Codes: React.FC = () => {
  const [codes, setCodes] = useState<RedemptionCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    loadCodes();
  }, []);

  const loadCodes = async () => {
    try {
      const response = await api.get('/payment/codes?limit=50');
      setCodes(response.data.codes || []);
    } catch (error: any) {
      console.error('加载兑换码失败:', error);
      message.error(`加载兑换码失败: ${error.response?.data?.message || error.message || '未知错误'}`);
      setCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCodes = async (values: {
    amount: number;
    type: string;
    count: number;
    expiresInDays?: number;
  }) => {
    setCreateLoading(true);
    try {
      await api.post('/payment/codes', values);
      message.success(`成功生成 ${values.count} 个兑换码`);
      setCreateModalVisible(false);
      loadCodes();
    } catch (error: any) {
      message.error(error.response?.data?.message || '生成兑换码失败');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteCode = async (id: number) => {
    try {
      await api.delete(`/payment/codes/${id}`);
      message.success('删除成功');
      loadCodes();
    } catch (error: any) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const getStatusTag = (code: RedemptionCode) => {
    if (code.used_by_name) {
      return <Tag color="green">已使用</Tag>;
    }
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return <Tag color="red">已过期</Tag>;
    }
    return <Tag color="blue">未使用</Tag>;
  };

  const columns = [
    {
      title: '兑换码',
      dataIndex: 'code',
      key: 'code',
      width: 200,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount: any) => `¥${(parseFloat(amount) || 0).toFixed(2)}`,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => type === 'balance' ? '余额' : '服务器时长',
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_: any, record: RedemptionCode) => getStatusTag(record),
    },
    {
      title: '使用者',
      dataIndex: 'used_by_name',
      key: 'used_by_name',
      width: 120,
      render: (name: string) => name || '-',
    },
    {
      title: '使用时间',
      dataIndex: 'used_at',
      key: 'used_at',
      width: 150,
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 150,
      render: (date: string) => date ? new Date(date).toLocaleString() : '永不过期',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, record: RedemptionCode) => (
        <Space>
          {!record.used_by_name && (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCode(record.id)}
            >
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="fade-in">
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">兑换码管理</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            生成兑换码
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={codes}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个兑换码`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 生成兑换码模态框 */}
      <Modal
        title="生成兑换码"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
      >
        <Form onFinish={handleCreateCodes} layout="vertical">
          <Form.Item
            name="amount"
            label="金额"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber
              min={0.01}
              max={10000}
              step={0.01}
              placeholder="输入金额"
              className="w-full"
              addonAfter="元"
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
            initialValue="balance"
          >
            <Select>
              <Option value="balance">余额</Option>
              <Option value="server_time">服务器时长</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="count"
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
            initialValue={1}
          >
            <InputNumber
              min={1}
              max={100}
              placeholder="输入生成数量"
              className="w-full"
            />
          </Form.Item>

          <Form.Item
            name="expiresInDays"
            label="有效期（天）"
            help="留空表示永不过期"
          >
            <InputNumber
              min={1}
              max={365}
              placeholder="输入有效期天数"
              className="w-full"
            />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => setCreateModalVisible(false)}>
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={createLoading}>
              生成
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Codes;



